import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

export interface ConfirmationOptions {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmButtonClass?: string;
    onConfirm: () => void | Promise<void>;
    onCancel?: () => void;
}

/**
 * Reusable confirmation dialog utility
 * Provides consistent confirmation dialogs across the application
 */
export const showConfirmation = (options: ConfirmationOptions) => {
    const {
        title,
        message,
        confirmLabel = "Confirm",
        cancelLabel = "Cancel",
        confirmButtonClass = "btn-danger",
        onConfirm,
        onCancel
    } = options;

    confirmAlert({
        title,
        message,
        buttons: [
            {
                label: confirmLabel,
                onClick: async () => {
                    try {
                        const result = onConfirm();
                        // Handle async confirmations
                        if (result instanceof Promise) {
                            await result;
                        }
                    } catch (error) {
                        console.error("Confirmation action failed:", error);
                    }
                }
            },
            {
                label: cancelLabel,
                onClick: onCancel || (() => { })
            }
        ],
        closeOnEscape: true,
        closeOnClickOutside: true
    } as any);
};

/**
 * Confirmation for removing/deleting items
 */
export const confirmDelete = (
    itemName: string,
    itemType: string,
    onConfirm: () => void | Promise<void>
) => {
    showConfirmation({
        title: `Remove ${itemType}`,
        message: `Are you sure you want to remove ${itemName ? `"${itemName}"` : `this ${itemType.toLowerCase()}`}?`,
        confirmLabel: "Remove",
        cancelLabel: "Cancel",
        confirmButtonClass: "btn-danger",
        onConfirm
    });
};

/**
 * Confirmation for removing users
 */
export const confirmUserRemove = (
    userName: string,
    onConfirm: () => void | Promise<void>
) => {
    showConfirmation({
        title: "Remove User",
        message: `Are you sure you want to remove user "${userName}"? This action cannot be undone.`,
        confirmLabel: "Remove",
        cancelLabel: "Cancel",
        confirmButtonClass: "btn-danger",
        onConfirm
    });
};

/**
 * Confirmation for removing preferences
 */
export const confirmPreferenceRemove = (
    preferenceName: string,
    onConfirm: () => void | Promise<void>
) => {
    showConfirmation({
        title: "Remove Preference",
        message: `Are you sure you want to remove "${preferenceName}" from your preferences?`,
        confirmLabel: "Remove",
        cancelLabel: "Cancel",
        confirmButtonClass: "btn-danger",
        onConfirm
    });
};

